import { isLocalExperimentMode } from "./localMode";
import {
  getExperimentByKey,
  getExperimentVariantByKey,
  listExperiments,
} from "./registry";
import { safeLocalStorage } from "../../utils/safeStorage";

export const EXPERIMENT_PREVIEW_PARAM = "ll_preview";
export const EXPERIMENT_PREVIEW_OVERRIDES_PARAM = "ll_exp";
export const EXPERIMENT_PREVIEW_CHANGE_EVENT = "ll:experiment-preview-change";
export const FUNNEL_EXPERIMENT_SUBJECT_STORAGE_KEY =
  "funnelExperimentSubjectId:v1";

const SURFACES_BY_PATH = [
  {
    match: (path) => path === "/lab/ui/public/landing-section-order",
    surfaces: ["join_landing"],
  },
  {
    match: (path) => path === "/" || path === "/join",
    surfaces: ["join_landing", "pre_onboarding_flow"],
  },
  {
    match: (path) =>
      ["/get-started", "/signup", "/join-waitlist", "/member/signup"].includes(
        path,
      ),
    surfaces: ["pre_onboarding_flow"],
  },
  {
    match: (path) => path === "/dashboard" || path.startsWith("/dashboard/"),
    surfaces: [
      "member_dashboard",
      "member_onboarding_address_card",
      "booking_flow",
    ],
  },
];

const normalizeKey = (value = "") =>
  String(value || "")
    .trim()
    .toLowerCase();

const getWindowLocation = () => {
  if (typeof window === "undefined") {
    return {
      href: "",
      search: "",
      pathname: "",
      origin: "",
    };
  }

  return window.location;
};

const getSearchParams = (search = "") => {
  const normalizedSearch = String(search || "");
  return new URLSearchParams(
    normalizedSearch.startsWith("?")
      ? normalizedSearch.slice(1)
      : normalizedSearch,
  );
};

const serializeOverrides = (overrides = {}) =>
  Object.entries(overrides)
    .map(([experimentKey, variantKey]) => [
      normalizeKey(experimentKey),
      normalizeKey(variantKey),
    ])
    .filter(([experimentKey, variantKey]) => experimentKey && variantKey)
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([experimentKey, variantKey]) => `${experimentKey}:${variantKey}`)
    .join(",");

export const isExperimentPreviewEnvironment = () => isLocalExperimentMode();

export const parseExperimentPreviewOverrides = (search = "") => {
  const params = getSearchParams(search);
  const rawValue = params.get(EXPERIMENT_PREVIEW_OVERRIDES_PARAM) || "";
  const tokens = rawValue
    .split(",")
    .map((token) => token.trim())
    .filter(Boolean);
  const rawOverrides = {};
  const requestedExperimentKeys = [];
  const malformedTokens = [];

  tokens.forEach((token) => {
    const [experimentKey, variantKey, ...extraParts] = token.split(":");
    const normalizedExperimentKey = normalizeKey(experimentKey);
    const normalizedVariantKey = normalizeKey(variantKey);

    if (!normalizedExperimentKey || extraParts.length > 0) {
      malformedTokens.push({
        token,
        reason: "invalid_format",
      });
      return;
    }

    if (!token.includes(":")) {
      requestedExperimentKeys.push(normalizedExperimentKey);
      return;
    }

    if (!normalizedVariantKey) {
      malformedTokens.push({
        token,
        reason: "invalid_format",
      });
      return;
    }

    rawOverrides[normalizedExperimentKey] = normalizedVariantKey;
  });

  return {
    rawValue,
    rawOverrides,
    requestedExperimentKeys: [...new Set(requestedExperimentKeys)],
    malformedTokens,
  };
};

export const getExperimentPreviewState = (locationLike = getWindowLocation()) => {
  const search = String(locationLike?.search || "");
  const params = getSearchParams(search);
  const previewRequested = params.get(EXPERIMENT_PREVIEW_PARAM) === "1";
  const hasOverrideParam = params.has(EXPERIMENT_PREVIEW_OVERRIDES_PARAM);
  const requested = previewRequested || hasOverrideParam;
  const isAvailable = isExperimentPreviewEnvironment();
  const { rawOverrides, requestedExperimentKeys, malformedTokens } =
    parseExperimentPreviewOverrides(search);
  const overrides = {};
  const validRequestedExperimentKeys = [];
  const invalidOverrides = [...malformedTokens];

  requestedExperimentKeys.forEach((experimentKey) => {
    const experiment = getExperimentByKey(experimentKey);
    if (!experiment?.key) {
      invalidOverrides.push({
        token: experimentKey,
        experimentKey,
        reason: "unknown_experiment",
      });
      return;
    }

    validRequestedExperimentKeys.push(experimentKey);
  });

  Object.entries(rawOverrides).forEach(([experimentKey, variantKey]) => {
    const experiment = getExperimentByKey(experimentKey);
    if (!experiment?.key) {
      invalidOverrides.push({
        experimentKey,
        variantKey,
        reason: "unknown_experiment",
      });
      return;
    }

    const variant = getExperimentVariantByKey(experimentKey, variantKey);
    if (!variant?.key) {
      invalidOverrides.push({
        experimentKey,
        variantKey,
        reason: "unknown_variant",
      });
      return;
    }

    overrides[experimentKey] = variantKey;
  });

  return {
    isAvailable,
    isPreviewMode: Boolean(isAvailable && requested),
    isPreviewConsoleVisible: Boolean(isAvailable && previewRequested),
    requested,
    previewRequested,
    hasOverrideParam,
    overrides,
    requestedExperimentKeys: validRequestedExperimentKeys,
    invalidOverrides,
  };
};

export const isExperimentPreviewMode = (
  locationLike = getWindowLocation(),
) => getExperimentPreviewState(locationLike).isPreviewMode;

export const getExperimentPreviewOverride = (
  experimentKey = "",
  state = getExperimentPreviewState(),
) => {
  if (!state?.isPreviewMode) return null;

  const resolvedExperimentKey = normalizeKey(experimentKey);
  const variantKey = normalizeKey(state?.overrides?.[resolvedExperimentKey]);
  if (!resolvedExperimentKey || !variantKey) return null;

  const variant = getExperimentVariantByKey(resolvedExperimentKey, variantKey);
  if (!variant?.key) return null;

  return {
    experimentKey: resolvedExperimentKey,
    variantKey,
    variant,
    source: "url_preview",
  };
};

export const buildExperimentPreviewUrl = ({
  href = getWindowLocation().href,
  overrides = {},
  previewMode = false,
} = {}) => {
  const url = new URL(
    href || "/",
    getWindowLocation().origin || "http://localhost",
  );
  const params = new URLSearchParams(url.search);
  params.delete(EXPERIMENT_PREVIEW_PARAM);
  params.delete(EXPERIMENT_PREVIEW_OVERRIDES_PARAM);

  const serializedOverrides = serializeOverrides(overrides);
  const pieces = [];
  params.forEach((value, key) => {
    pieces.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
  });

  if (serializedOverrides) {
    pieces.push(
      `${EXPERIMENT_PREVIEW_OVERRIDES_PARAM}=${serializedOverrides}`,
    );
  }

  if (previewMode) {
    pieces.push(`${EXPERIMENT_PREVIEW_PARAM}=1`);
  }

  url.search = pieces.length ? `?${pieces.join("&")}` : "";
  return url.toString();
};

export const replaceExperimentPreviewUrl = ({
  overrides = {},
  previewMode = false,
} = {}) => {
  if (typeof window === "undefined") return "";

  const nextUrl = buildExperimentPreviewUrl({
    href: window.location.href,
    overrides,
    previewMode,
  });
  window.history.replaceState(window.history.state, "", nextUrl);
  window.dispatchEvent(new Event(EXPERIMENT_PREVIEW_CHANGE_EVENT));
  return nextUrl;
};

export const getStableFunnelExperimentSubjectId = () => {
  if (typeof window === "undefined") return "";

  const existing = safeLocalStorage.getItem(
    FUNNEL_EXPERIMENT_SUBJECT_STORAGE_KEY,
  );
  if (existing) return existing;

  const next = `fx_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
  safeLocalStorage.setItem(FUNNEL_EXPERIMENT_SUBJECT_STORAGE_KEY, next);
  return next;
};

export const getExperimentPreviewSurfacesForPath = (pathname = "") => {
  const normalizedPath = String(pathname || "/").trim() || "/";
  const match = SURFACES_BY_PATH.find((entry) => entry.match(normalizedPath));
  return match ? [...match.surfaces] : [];
};

export const listExperimentsForPreviewSurface = ({
  pathname = getWindowLocation().pathname,
  overrides = {},
  requestedExperimentKeys = [],
  includeInactive = true,
} = {}) => {
  const surfaces = getExperimentPreviewSurfacesForPath(pathname);
  const surfaceSet = new Set(surfaces);
  const overrideKeys = new Set(Object.keys(overrides || {}));
  const requestedKeys = new Set(requestedExperimentKeys || []);
  const experiments = listExperiments({ includeInactive });

  const relevant = experiments.filter((experiment) => {
    if (overrideKeys.has(experiment?.key)) return true;
    if (requestedKeys.has(experiment?.key)) return true;
    const experimentSurfaces = Array.isArray(experiment?.surfaces)
      ? experiment.surfaces
      : [];
    return experimentSurfaces.some((surface) => surfaceSet.has(surface));
  });

  if (relevant.length > 0) {
    return relevant;
  }

  return experiments.filter((experiment) => experiment?.status === "active");
};
